/**
 * Evidence workspace for the selected statement: text, rating control,
 * evidence timeline and the mentor comment.
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import { getLevelForm, type IcfFormLevel, type StatementRatingValue } from '@/data/icfFormStatements';
import type { EngagementLanguage, EvidenceFeedbackReason, EvidenceFeedbackVerdict, SessionObservationForm, SofOverrideReason } from '@/types/mentorEngagements';
import { TextField, mentorStyles } from '../shared';
import { deriveReviewState, type SofReviewState } from '../sofReview';
import { EvidenceTimeline } from './EvidenceTimeline';
import { SofRatingControl } from './SofRatingControl';
import { StatusDot } from './SofStatementRail';
import { allSupportRejected, buildRelevanceLookup, buildTimeline, railStateFor, type EvidenceMoment, type ReviewItem } from './model';

interface Props {
  item: ReviewItem;
  level: IcfFormLevel;
  language: EngagementLanguage;
  form: SessionObservationForm;
  state: SofReviewState;
  locked: boolean;
  disabled: boolean;
  recorded: boolean;
  transcriptAvailable: boolean;
  expanded: boolean;
  onChangeRating: (id: string, value: StatementRatingValue) => void;
  onUseAi: (id: string) => void;
  onOverrideReason: (id: string, reason: SofOverrideReason | null) => void;
  onComment: (id: string, comment: string) => void;
  onToggleExpanded: (id: string) => void;
  onVerdict: (id: string, moment: EvidenceMoment, verdict: EvidenceFeedbackVerdict, reason?: EvidenceFeedbackReason) => void;
  onJump: (moment: EvidenceMoment) => void;
}

export function SofStatementPane({
  item, level, language, form, state, locked, disabled, recorded, transcriptAvailable, expanded,
  onChangeRating, onUseAi, onOverrideReason, onComment, onToggleExpanded, onVerdict, onJump,
}: Props) {
  const { t } = useTranslation();
  const id = item.id;
  const ai = form.ai_ratings[id];
  const touched = Object.prototype.hasOwnProperty.call(state.mentorRatings, id);
  const mentor = touched ? state.mentorRatings[id] ?? undefined : form.mentor_ratings[id];
  const review = deriveReviewState(ai, mentor, level);
  const moments = buildTimeline(ai);
  const lookup = buildRelevanceLookup(form.evidence_feedback, state.evidenceFeedback, mentor);
  const allRejected = allSupportRejected(moments, lookup);
  const railState = railStateFor(review.state, allRejected);
  const catalog = getLevelForm(level);
  const competency = catalog.competencies.find(block => block.code === item.competency);
  const text = language === 'tr' ? item.statement.text_tr : item.statement.text_en;
  const note = language === 'tr' ? item.statement.note_tr : item.statement.note_en;
  const comment = mentor?.comment ?? '';

  return (
    <View testID="sof_review_pane" style={styles.pane}>
      <View style={styles.eyebrow}>
        <StatusDot state={railState} />
        <Text style={styles.eyebrowText}>{competency ? `${competency.code} · ${language === 'tr' ? competency.title_tr : competency.title_en}` : item.competency}</Text>
        <Text style={styles.stateText}>{t(`mentorEngagements.review.state.${railState === 'ai_suggested' ? 'aiSuggested' : railState === 'evidence_rejected' ? 'evidenceRejected' : railState}`)}</Text>
      </View>
      <View testID={`sof_review_statement_${id}`} style={styles.statement}>
        <Text style={styles.statementId}>{id}</Text>
        <Text style={styles.statementText}>{text}</Text>
        {note ? <Text style={mentorStyles.mutedText}>{note}</Text> : null}
      </View>

      <SofRatingControl
        id={id}
        level={level}
        language={language}
        options={catalog.scale.options}
        value={review.value}
        aiValue={review.aiValue}
        state={review.state}
        confidence={ai?.confidence}
        disabled={disabled}
        locked={locked}
        showPcc64Note={id === '6.4'}
        overrideReason={mentor?.override_reason}
        onChange={value => onChangeRating(id, value)}
        onUseAi={() => onUseAi(id)}
        onOverrideReason={reason => onOverrideReason(id, reason)}
      />

      {!recorded ? (
        <View style={mentorStyles.row}>
          <TextField label={t('mentorEngagements.review.comment')} value={comment} editable={!disabled && review.value !== null} multiline
            onChangeText={value => onComment(id, value)} />
        </View>
      ) : null}

      <EvidenceTimeline
        statementId={id}
        ai={ai}
        moments={moments}
        lookup={lookup}
        expanded={expanded}
        locked={locked}
        recorded={recorded}
        transcriptAvailable={transcriptAvailable}
        allRejected={allRejected}
        onToggleExpanded={() => onToggleExpanded(id)}
        onVerdict={(moment, verdict, reason) => onVerdict(id, moment, verdict, reason)}
        onReopen={moment => onVerdict(id, moment, 'relevant')}
        onJump={onJump}
      />

      {recorded ? (
        <View style={mentorStyles.row}>
          <TextField label={t('mentorEngagements.review.comment')} value={comment} editable={!disabled && review.value !== null} multiline
            onChangeText={value => onComment(id, value)} />
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  pane: { gap: 18, minWidth: 0 },
  eyebrow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  eyebrowText: { color: reviewDesk.inkSoft, fontSize: 12, fontWeight: '700', flexShrink: 1 },
  stateText: { color: reviewDesk.muted, fontSize: 12, fontWeight: '600' },
  statement: { gap: 4, minWidth: 0 },
  statementId: { color: reviewDesk.accent, fontSize: 13, fontWeight: '800', fontVariant: ['tabular-nums'] },
  statementText: { color: reviewDesk.ink, fontSize: 18, lineHeight: 26, fontWeight: '700' },
});
